import type { Capitolo } from '../tipi.ts'

const capitolo: Capitolo = {
  id: 'albo-mediatori',
  titolo: 'L’albo dei mediatori',
  gruppo: 'Mediazioni',
  permesso: ['mediatore.leggi', 'mediatore.gestisci'],
  aCheServe: 'La voce «Mediatori» è l’albo dei mediatori linguistico-culturali di CIDIS, interni ed esterni, con '
    + 'lingue e sedi. Chi gestisce l’albo aggiunge i mediatori, dà loro l’accesso al gestionale e li importa da Excel.',
  parti: [
    {
      titolo: 'Trovare un mediatore',
      passi: [
        'Apri «Mediatori». L’elenco mostra nome, rapporto, comune in cui lavora, lingue, sedi e se usa il gestionale.',
        'Restringi con «Cerca per nome», «Quali» (attivi, non più attivi, tutti), «Sede», «Lingua» e «Comune».',
        'Chi gestisce l’albo ha anche il filtro «Paese di origine».',
      ],
      immagine: {
        file: 'albo.webp', larghezza: 1200, altezza: 800,
        descrizione: 'L’albo dei mediatori con i filtri Sede, Lingua e Paese di origine e i comandi in alto.',
      },
      nota: 'Sotto il nome leggi «Dal vecchio archivio» per i mediatori importati e «Anche: …» per i nomi dei '
        + 'doppioni già uniti: la ricerca trova anche quelli. «comune mancante» segna i mediatori che non hanno '
        + 'ancora il comune in cui lavorano: «Comune» → «— non indicato —» li trova tutti.',
    },
    {
      titolo: 'Aggiungere, modificare, disattivare',
      permesso: 'mediatore.gestisci',
      passi: [
        '«Aggiungi un mediatore»: nome, cognome, rapporto con CIDIS, «Comune in cui lavora» (obbligatorio), '
          + '«Recapito» (facoltativo), lingue e «Sedi CIDIS in cui lavora» (oppure «Lavora in tutte le sedi»). In '
          + '«Anagrafica e contratto» puoi aggiungere N. albo, email, residenza, paese di origine e contratto.',
        '«Modifica» nella riga corregge i dati. Il comune in cui lavora è obbligatorio anche qui: su chi non '
          + 'l’ha, sceglilo prima di salvare.',
        '«Disattiva» toglie dalle nuove mediazioni chi non lavora più con CIDIS: resta fra i «Non più attivi», '
          + 'con il suo storico. «Riattiva» lo rimette.',
      ],
      nota: 'Le sedi servono a trovare il mediatore, non cambiano quello che vede: vede solo le mediazioni affidate a lui. '
        + 'Il comune in cui lavora non è la residenza: lo vede chiunque legga l’albo. Il recapito è dove riceve le '
        + 'persone: lo vede chi programma una mediazione con lui, dopo averla programmata, e non va negli avvisi né '
        + 'nelle email.',
    },
    {
      titolo: '«Dai l’accesso» a un mediatore',
      permesso: 'mediatore.gestisci',
      testo: [
        'Serve solo a chi deve entrare nel gestionale per vedere le sue mediazioni e segnarne l’esito.',
      ],
      passi: [
        'Nella riga del mediatore premi «Dai l’accesso».',
        'Controlla l’«Email» (è proposta quella dell’anagrafica), l’«Area dell’incarico» e le sedi. Per un '
          + 'esterno indica la «Scadenza dell’utenza», al massimo un anno.',
        'Premi «Dai l’accesso». La finestra mostra il collegamento da mandargli e il **codice da dettargli a voce**.',
        'Finché non entra, leggi «Invitato, non ancora entrato». Se l’invito scade, premi «Nuovo invito».',
      ],
      nota: 'Coordinatori e Project Manager danno l’accesso solo con un’email che il gestionale non conosce ancora. '
        + 'Per collegare l’utenza di un collega serve la Direzione.',
    },
    {
      titolo: 'Importare i mediatori da Excel',
      permesso: 'mediatore.gestisci',
      passi: [
        'Premi «Importa da Excel», poi «Scarica il tracciato».',
        'Nel foglio «Mediatori» scrivi una riga per mediatore, al massimo 200. Nome e cognome sono obbligatori; '
          + 'indica anche il «Comune in cui lavora» (senza, la riga entra con un avviso e va completata dopo). '
          + 'Lingue, sedi, rapporto, paese e contratto si scelgono dalla tendina. Non cambiare i nomi delle colonne.',
        'Salva il file e premi «Scegli il foglio compilato».',
        'Controlla l’anteprima: ogni riga è «Pronta», ha un errore da correggere oppure è «Già nell’albo, si salta».',
        'Premi «Importa … mediatori».',
      ],
      immagine: {
        file: 'albo-importa.webp', larghezza: 1200, altezza: 800,
        descrizione: 'La finestra Importa da Excel con i comandi Scarica il tracciato e Scegli il foglio compilato.',
      },
      nota: 'Entrano solo i mediatori nuovi: chi c’è già non si aggiorna. Per farli entrare nel gestionale usa poi '
        + '«Dai l’accesso».',
    },
    {
      titolo: 'Scaricare l’elenco',
      permesso: ['mediatore.esporta', 'mediatore.gestisci'],
      testo: [
        '«Scarica l’elenco», in alto, scarica un foglio che si apre con Excel, con tutti i mediatori e una colonna '
          + 'vuota «Lavora ancora con CIDIS? (sì/no)» da far compilare alle sedi. Ogni scaricamento resta nel '
          + 'registro eventi: conserva il file con cura.',
      ],
    },
    {
      titolo: 'Unire i doppioni',
      permesso: 'mediatore.gestisci',
      passi: [
        'Premi «Unisci i doppioni»: la finestra propone i nomi che probabilmente sono la stessa persona.',
        'Premi «Rivedi», scegli con «Tieni» il mediatore da tenere e spunta i doppioni.',
        'Premi «Controlla l’unione», leggi i numeri e conferma.',
      ],
      nota: 'L’unione non si annulla: controlla bene prima di confermare.',
    },
  ],
  avvertenze: [
    {
      quando: '«Indica il comune in cui lavora il mediatore: è obbligatorio.»',
      cosaFare: 'Scegli il «Comune in cui lavora» fra i suggerimenti, poi salva. Vale anche per i mediatori inseriti '
        + 'prima, quando li modifichi.',
    },
    {
      quando: 'Il gestionale non disattiva un mediatore.',
      cosaFare: 'Ha mediazioni in programma: affidale a un altro mediatore o annullale, poi riprova.',
    },
    {
      quando: 'Nell’importazione una riga dice che un valore non è nell’elenco.',
      cosaFare: 'Scegli il valore dalla tendina del foglio invece di scriverlo a mano, poi carica di nuovo il foglio.',
    },
    {
      quando: '«Dai l’accesso» si ferma con l’email di un collega.',
      cosaFare: 'Coordinatori e Project Manager creano solo utenze nuove: chiedi alla Direzione di collegare '
        + 'l’utenza che c’è già.',
    },
  ],
  vediAnche: ['mediazioni', 'utenti'],
  parole: ['albo', 'mediatori', 'excel', 'xlsx', 'importa', 'tracciato', 'lingua', 'paese', 'accesso', 'doppioni',
    'comune', 'recapito', 'indirizzo'],
}

export default capitolo
